import { COLORS, DATASET_INDICES } from '../constants.js';
import { Tooltip } from 'chart.js';

// Helper function to get tooltip data from chart
const getTooltipData = (context) => {
  const chart = context.chart || context[0]?.chart;
  const dataIndex =
    context.dataIndex !== undefined ? context.dataIndex : context[0]?.dataIndex;

  if (!chart || dataIndex === undefined) return null;

  const fullDateLabels = chart.data.fullDateLabels;
  const tooltipDataMap = chart.data.tooltipDataMap;
  const projectedFlags = chart.data.projectedFlags;

  return {
    chart,
    dataIndex,
    fullDateLabels,
    tooltipDataMap,
    projectedFlags,
    isProjected: projectedFlags && projectedFlags[dataIndex],
  };
};

// Helper function to format date for tooltip
const formatTooltipDate = (dateString, isProjected) => {
  const date = new Date(dateString);
  const today = new Date();
  const timeDiff = Math.abs(today - date);
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  const isToday = daysDiff <= 1;

  if (isProjected && isToday) {
    return 'If you apply today...';
  } else {
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    return `If you applied on ${formattedDate}...`;
  }
};

// Custom positioner: positions tooltip at mean dataset point with edge handling
export const meanDatasetPositioner = function (elements, eventPosition) {
  const meanElementDataset = elements.find(
    (el) => el.datasetIndex === DATASET_INDICES.MEAN,
  );

  if (!meanElementDataset) {
    return Tooltip.positioners.average.call(this, elements, eventPosition);
  }

  const meanElement = meanElementDataset.element;
  const chart = this.chart;
  const tooltipWidth = 250; // Approximate tooltip width
  const edgeThreshold = tooltipWidth / 2 + 20; // When to switch to side positioning

  const distanceFromLeftEdge = meanElement.x - chart.chartArea.left;

  const distanceFromRightEdge = chart.chartArea.right - meanElement.x;

  if (distanceFromLeftEdge < edgeThreshold / 2) {
    // Near left edge
    return {
      x: meanElement.x + 10,
      y: meanElement.y,
      xAlign: 'left',
      yAlign: 'center',
    };
  } else if (distanceFromRightEdge < edgeThreshold) {
    // Near right edge
    return {
      x: meanElement.x - 10,
      y: meanElement.y,
      xAlign: 'right',
      yAlign: 'center',
    };
  } else {
    // Normal case - position tooltip above the point
    return {
      x: meanElement.x,
      y: meanElement.y - 10,
      xAlign: 'center',
      yAlign: 'bottom',
    };
  }
};

// Tooltip title callback - shows "PROJECTED" label for projected data
export const tooltipTitleCallback = function (context) {
  if (!context || context.length === 0) return '';

  const tooltipData = getTooltipData(context[0]);
  return tooltipData?.isProjected ? 'PROJECTED' : '';
};

// Tooltip label callback - formats date
export const tooltipLabelCallback = function (context) {
  if (!context || context.datasetIndex !== DATASET_INDICES.MEAN) return '';

  const tooltipData = getTooltipData(context);
  if (!tooltipData || !tooltipData.fullDateLabels) return 'No date available';

  const dateString = tooltipData.fullDateLabels[tooltipData.dataIndex];
  return dateString
    ? formatTooltipDate(dateString, tooltipData.isProjected)
    : 'No date available';
};

// Tooltip footer callback - formats the main tooltip content
export const tooltipFooterCallback = function (tooltipItems) {
  if (!tooltipItems || tooltipItems.length === 0) return '';

  const meanItem = tooltipItems.find(
    (item) => item.datasetIndex === DATASET_INDICES.MEAN,
  );
  if (!meanItem) return '';

  const tooltipData = getTooltipData(meanItem);
  if (
    !tooltipData ||
    !tooltipData.fullDateLabels ||
    !tooltipData.tooltipDataMap
  ) {
    return 'No data available';
  }

  const dateKey = tooltipData.fullDateLabels[tooltipData.dataIndex];
  const data = tooltipData.tooltipDataMap[dateKey];
  if (!data) return 'No data available';

  const { Estimate: mean, '5th Percentile': p5, '95th Percentile': p95 } = data;

  if (tooltipData.isProjected) {
    return [
      `Most people will probably receive their EIN in ${p5}–${p95}`,
      `business days, most likely closer to ${mean} business days.`,
    ];
  } else {
    return [
      `Most people received their EIN in ${p5}–${p95} business`,
      `days, with the average being ${mean} business days.`,
    ];
  }
};

// Tooltip filter function - only show tooltip for Mean dataset
export const tooltipFilter = function (tooltipItem) {
  return tooltipItem.datasetIndex === DATASET_INDICES.MEAN;
};

export const tooltipConfig = {
  enabled: true,
  mode: 'index',
  position: 'meanDataset',
  intersect: false,
  borderWidth: 1,
  cornerRadius: 8,
  caretSize: 6,
  displayColors: false,
  backgroundColor: COLORS.white,
  borderColor: COLORS.tooltipBorder,
  titleFont: {
    family: 'Graphik, sans-serif',
    lineHeight: 1.2,
    weight: 500,
    size: 12,
  },
  titleMarginBottom: 0,
  titleColor: COLORS.projectedLabel,
  titleSpacing: 0,
  bodyFont: {
    family: 'Graphik, sans-serif',
    lineHeight: 1.5,
    weight: 500,
    size: 12,
  },
  bodyColor: COLORS.primaryText,
  bodySpacing: 0,
  footerFont: {
    family: 'Graphik, sans-serif',
    lineHeight: 1.17,
    weight: 400,
    size: 12,
  },
  footerColor: COLORS.primaryText,
  footerMarginTop: 0,
  padding: {
    left: 12,
    right: 12,
    top: 8,
    bottom: 12,
  },
  filter: tooltipFilter,
  callbacks: {
    title: tooltipTitleCallback,
    label: tooltipLabelCallback,
    footer: tooltipFooterCallback,
  },
};

// Registers the custom tooltip positioner with Chart.js
export const registerTooltipPositioner = () => {
  Tooltip.positioners.meanDataset = meanDatasetPositioner;
};
